import React, { FC, useEffect, useState } from "react";
import { Node, Connection, Edge, useReactFlow, useUpdateNodeInternals } from "reactflow";
import { AddMenuNodePopulater, IAutoConnectMeta } from "./AddMenuNodePopulation";
import "./AddNodeMenu.scss";
import { forceNodeUpdate } from "../Util/NodeUtil";
import { createNewNodeMeta, getNewId } from "../DesignerContainer/DesignerContainer";
import { getEdgeId, getNameFromDataHandleId, handleEdgeConnection, handleEdgeConnections } from "../Util/EdgeUtil";
import { getAnchorProperties, handleAnchorDestinationLayoutChanged, handleAnchorNodeDrop } from "../Util/AnchorUtil";
import { useViewController } from "../ViewContext";
import { getServiceIconBackgroundClassName, getServiceIconClassName } from "../NodeTypes/Base";

export interface INodeHandleProps {
  nodeId: string;
  nodeType: string;
  handleId: string;
}

export interface IAddNodeMenuProps {
  dragFromNode?: INodeHandleProps;
  dragToNode?: INodeHandleProps;
  addNodeMenuContainerRef?: React.RefObject<HTMLDivElement>;
  reactflowContainer?: DOMRect;
  onAdd?: () => void;
  onClose?: () => void;
}

export const AddNodeMenu: FC<IAddNodeMenuProps> = ({
  dragFromNode,
  dragToNode,
  addNodeMenuContainerRef,
  reactflowContainer,
  onAdd,
  onClose
}) => {
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const [dockedHoveredItemId, setDockedHoveredItemId] = useState<string | null>(null);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const [, setSearchFocusState] = useState(false);

  const experimentalModeState = useViewController().experimentalModeState;

  let nodeMapperTypes: Map<string, IAutoConnectMeta> | undefined = undefined;

  if (dragToNode && dragFromNode) {
    nodeMapperTypes = AddMenuNodePopulater.getValidActionsForConnection(dragFromNode, dragToNode, reactFlowInstance);
  } else if (dragFromNode?.nodeId && dragFromNode?.nodeType && dragFromNode?.handleId) {
    nodeMapperTypes = AddMenuNodePopulater.getValidNodeTypesForGivenHandle(
      dragFromNode.nodeId,
      dragFromNode.nodeType,
      dragFromNode.handleId,
      experimentalModeState,
      reactFlowInstance
    );
  }

  const handleItemHover = (itemId: string | null, docked?: boolean) => {
    if (docked) {
      setDockedHoveredItemId(itemId);
    } else {
      setHoveredItemId(itemId);
    }
  };

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    // Prevent the wheel event from propagating up to the canvas
    event.stopPropagation();
  };

  const searchDropdown = () => {
    var input: any = document.getElementById("plex-fd-dropdown-search");
    var filter = input.value.toUpperCase();
    var dropdown: any = document.getElementById("plex-fd-dropdown-content");
    var options = dropdown.getElementsByTagName("a");
    for (var i = 0; i < options.length; i++) {
      var txtValue = options[i].textContent || options[i].innerText;
      if (txtValue.toUpperCase().indexOf(filter) > -1) {
        options[i].style.display = "";
      } else {
        options[i].style.display = "none";
      }
    }
  };

  const when = (condition: () => boolean, action: () => void) => {
    if (condition()) {
      action();
    } else {
      let checkIntervalCount = 0;
      const checkInterval = setInterval(() => {
        if (condition()) {
          clearInterval(checkInterval);
          action();
        }

        // Stop checking after 5 seconds.
        if (checkIntervalCount > 500) {
          clearInterval(checkInterval);
        }

        checkIntervalCount++;
      }, 10);
    }
  };

  const onClick = (itemId: string, docked?: boolean) => {
    if (!dragFromNode || !nodeMapperTypes) {
      return;
    }

    const toNodeMeta: IAutoConnectMeta = nodeMapperTypes.get(itemId)!;
    const fromNode = reactFlowInstance.getNode(dragFromNode.nodeId)!;
    const containerRect = addNodeMenuContainerRef?.current?.getBoundingClientRect();
    const containerProject = reactFlowInstance.project({ x: containerRect?.x ?? 0, y: containerRect?.y ?? 0 });
    const toNodeInit = {
      position: {
        x: containerProject.x - (reactflowContainer?.x ?? 0),
        y: containerProject.y - (reactflowContainer?.y ?? 0) + 35
      },
      id: getNewId(toNodeMeta.nodeType, reactFlowInstance),
      type: toNodeMeta.nodeType
    };

    const configProperties: any = {};
    if (toNodeMeta.configProperties?.configProperties) {
      toNodeMeta.configProperties?.configProperties.forEach(
        (property: { propertyName: string; propertyValue: string }) => {
          configProperties[property.propertyName] = property.propertyValue;
        }
      );
    }

    const toNode = AddMenuNodePopulater.nodeCreatorAndLinker(
      toNodeInit,
      {},
      false,
      configProperties,
      toNodeMeta.allowNodeSelection
    );

    // Position in-between from/to if not docked.
    if (dragFromNode && dragToNode && !docked) {
      const dragToNodeInstance = reactFlowInstance.getNode(dragToNode.nodeId);
      if (dragToNodeInstance) {
        toNode.position.x = (fromNode.position.x + dragToNodeInstance.position.x) * 0.5;
        toNode.position.y = (fromNode.position.y + dragToNodeInstance.position.y) * 0.5;
      }
    }

    if (toNodeMeta.handleId) {
      when(
        () => reactFlowInstance.getNode(toNode.id) != null,
        () => {
          const edgeConnection = toNodeMeta.isOutput
            ? {
                target: toNode.id,
                targetHandle: toNodeMeta.handleId,
                source: toNodeMeta.fromNodeHandle ? toNodeMeta.fromNodeHandle.nodeId : fromNode!.id,
                sourceHandle: toNodeMeta.fromNodeHandle ? toNodeMeta.fromNodeHandle.handleId : dragFromNode.handleId
              }
            : {
                target: fromNode.id,
                targetHandle: dragFromNode.handleId,
                source: toNode!.id,
                sourceHandle: toNodeMeta.handleId
              };
          let additionalConnections: Connection[] = [edgeConnection];
          if (toNodeMeta.createConnections) {
            toNodeMeta.createConnections.forEach((connection: Connection) => {
              if (toNodeMeta.isOutput) {
                connection.source = toNode.id;
              } else {
                connection.target = toNode.id;
              }
              additionalConnections.push(connection);
            });
          }
          let addNodeMenuCurrentNodes = reactFlowInstance.getNodes().concat(toNode);
          let connectionResult = handleEdgeConnection(edgeConnection, false, addNodeMenuCurrentNodes);

          // Need to connect these edges after the new node is initially connected,
          //   so that outputs are created from the new linked schema connection.
          if (additionalConnections.length > 1) {
            connectionResult = handleEdgeConnections(additionalConnections, false, addNodeMenuCurrentNodes);
            if (!toNodeMeta.isOutput) {
              Object.keys(toNode.data.nodeProperties.outputs).forEach((outputName: string) => {
                const outputIsConnected = additionalConnections.some(
                  (connection: Connection) =>
                    connection.targetHandle && getNameFromDataHandleId(connection.targetHandle) === outputName
                );
                toNode.data.nodeProperties.outputs[outputName].enabled = outputIsConnected;
              });
            }
            additionalConnections.forEach((connection: Connection) => {
              const connectionSourceNode = addNodeMenuCurrentNodes.find(
                (n: Node<any>) => n.id === (connection.source ?? "")
              );
              if (connectionSourceNode) {
                forceNodeUpdate(connectionSourceNode, updateNodeInternals, reactFlowInstance);
              }
              const connectionTargetNode = addNodeMenuCurrentNodes.find(
                (n: Node<any>) => n.id === (connection.target ?? "")
              );
              if (connectionTargetNode) {
                forceNodeUpdate(connectionTargetNode, updateNodeInternals, reactFlowInstance);
              }
            });
          }
          forceNodeUpdate(fromNode, updateNodeInternals, reactFlowInstance);
          forceNodeUpdate(toNode, updateNodeInternals, reactFlowInstance);

          if (docked) {
            let dockingToNode = fromNode;
            if (toNodeMeta.fromNodeHandle) {
              const metaFromNode = addNodeMenuCurrentNodes.find(
                (n: Node<any>) => n.id === (toNodeMeta.fromNodeHandle?.nodeId ?? "")
              );
              if (metaFromNode) {
                dockingToNode = metaFromNode;
              }
            }
            const newEdgesState = handleAnchorNodeDrop(
              toNode,
              connectionResult.newEdges,
              AddMenuNodePopulater._reactFlowInstance,
              updateNodeInternals,
              false,
              dockingToNode
            );
            AddMenuNodePopulater._reactFlowInstance.setEdges(newEdgesState);
            handleAnchorDestinationLayoutChanged(
              fromNode.id,
              AddMenuNodePopulater._reactFlowInstance,
              updateNodeInternals
            );
          }
        }
      );
    }

    if (docked) {
      setDockedHoveredItemId(itemId);
    } else {
      setHoveredItemId(itemId);
    }

    if (onAdd) {
      onAdd();
    }
  };

  const getAutoApplyItem = () =>
    Array.from(nodeMapperTypes?.entries() ?? []).find((e: [string, IAutoConnectMeta]) => e[1].autoApplyFirstItem);

  const willAutoApplyItem = () => {
    if (nodeMapperTypes && nodeMapperTypes.size > 0) {
      const autoApplyItem = getAutoApplyItem();
      if (autoApplyItem && autoApplyItem[1].autoApplyFirstItem) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    if (willAutoApplyItem() && nodeMapperTypes) {
      const autoApplyItem = getAutoApplyItem();
      if (autoApplyItem) {
        onClick(autoApplyItem[0], true);
      }
    }
  }, [nodeMapperTypes, willAutoApplyItem, getAutoApplyItem, onClick]);

  if (!dragFromNode?.nodeId || !dragFromNode?.nodeType || !dragFromNode?.handleId) {
    return <div />;
  }

  if ((nodeMapperTypes?.size ?? 0) == 0) {
    return <div />;
  }

  if (willAutoApplyItem()) {
    return <div />;
  }

  return (
    <div className="plex-fd-dropdown" onWheelCapture={handleWheel}>
      <div className="plex-fd-anm-modalHeaderWrapper">
        <div className="plex-fd-anm-modalHeader">
          <div className="modalTitle">Quick Add Node</div>
          <div className="plex-fd-anm-close-button" onClick={onClose} />
        </div>
      </div>

      <div id="plex-fd-dropdown-content" className="plex-fd-dropdown-content">
        <div className="plex-fd-anm-search-box-container">
          <input
            type="text"
            id="plex-fd-dropdown-search"
            className="plex-fd-dropdown-search"
            onKeyUp={searchDropdown}
            placeholder="Search..."
            autoFocus={true}
            onFocus={() => setSearchFocusState(true)}
            onBlur={() => setSearchFocusState(false)}
          />

          <div className="plex-fd-anm-magnifying-glass-icon" />
        </div>

        {Array.from(nodeMapperTypes?.entries() ?? []).map(([key, node]) => {
          const newNode = createNewNodeMeta(
            node.nodeType,
            null,
            null,
            null,
            null,
            0,
            0,
            undefined,
            undefined,
            null
          );
          const fromNode = reactFlowInstance.getNode(dragFromNode.nodeId)!;
          const newEdge: Edge = node.isOutput
            ? {
                id: "",
                target: newNode.id,
                targetHandle: node.handleId,
                source: fromNode!.id,
                sourceHandle: dragFromNode.handleId
              }
            : {
                id: "",
                target: fromNode.id,
                targetHandle: dragFromNode.handleId,
                source: newNode!.id,
                sourceHandle: node.handleId
              };
          newEdge.id = getEdgeId(newEdge);
          const nodeCanBeAnchored = getAnchorProperties(
            newNode,
            reactFlowInstance.getEdges().concat([newEdge]),
            fromNode,
            reactFlowInstance
          ).nodeCanBeAnchored;

          const firstButtonClassName = `${nodeCanBeAnchored ? "" : "plex-fd-not-anchored-item"} ${
            node.text === hoveredItemId ? "plex-fd-quick-add-button-hovered" : ""
          } plex-fd-quick-add-position plex-fd-quick-add-left-button nodrag ${node.nodeType}-node-icon`;
          const secondButtonClassName = `${nodeCanBeAnchored ? "plex-fd-node-can-be-anchored" : ""} ${
            node.text === dockedHoveredItemId ? "plex-fd-quick-add-button-hovered" : ""
          } plex-fd-quick-add-docked plex-fd-quick-add-right-button nodrag`;

          return (
            <div className="plex-fd-quick-add-row" key={node.text + "_container"}>
              <a
                href="#"
                className={firstButtonClassName}
                // Need draggable=true to enable onDragStart event, to disable drag propagation and stop further drag events.
                draggable={true}
                key={node.text + "_undocked"}
                id={node.text}
                onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                  e.preventDefault();
                  const itemId = e.currentTarget.id;
                  onClick(itemId);
                }}
                onMouseEnter={() => handleItemHover(node.text)}
                onMouseLeave={() => handleItemHover("")}
                onDragStart={(event: React.DragEvent<HTMLAnchorElement>) => {
                  event.stopPropagation();
                  event.preventDefault();
                  return false;
                }}
              >
                <div className={getServiceIconBackgroundClassName(newNode.data.nodeProperties.operationIcon)} />
                <div className={getServiceIconClassName(newNode.data.nodeProperties.operationIcon)} />
                <div className="plex-fd-quick-add-docked-title">{node.text}</div>
                <div
                  className={`${
                    nodeCanBeAnchored ? "plex-fd-node-can-be-anchored" : ""
                  } plex-fd-quick-add-docked-label`}
                />
              </a>
              {nodeCanBeAnchored ? (
                <a
                  href="#"
                  className={secondButtonClassName}
                  // Need draggable=true to enable onDragStart event, to disable drag propagation and stop further drag events.
                  draggable={true}
                  key={node.text}
                  id={node.text}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                    e.preventDefault();
                    const itemId = e.currentTarget.id;
                    onClick(itemId, nodeCanBeAnchored);
                  }}
                  onMouseEnter={() => handleItemHover(node.text, true)}
                  onMouseLeave={() => handleItemHover("", true)}
                  onDragStart={(event: React.DragEvent<HTMLAnchorElement>) => {
                    event.stopPropagation();
                    event.preventDefault();
                    return false;
                  }}
                >
                  <div
                    className={nodeCanBeAnchored ? "plex-fd-node-can-be-anchored plex-fd-quick-add-dock-icon" : ""}
                  />
                </a>
              ) : (
                <></>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
